import { analytics } from "@/telemetry/amplitude"
import { dialogsAtom } from "@/store/dialogs-state"
import { type HotkeyCallback, type Options as HotkeysOptions, useHotkeys } from "react-hotkeys-hook"
import { useAtomValue } from "jotai"
import { isPhotoFullscreenAtom } from "@/store/ui-views-state"

type HotkeysEvent = Parameters<HotkeyCallback>[1]

const matchesProducedCharacter = (event: KeyboardEvent, hotkey: HotkeysEvent): boolean => {
	const key = event.key.toLowerCase()
	// event.key is the produced character, while event.code is the physical key position
	// (for example, AZERTY can report key "z" with code "KeyW"). react-hotkeys-hook can
	// still fall back to event.code with useKey enabled, so printable characters must match
	// the dispatched hotkey candidate exactly. Named keys and the "space" alias stay
	// library-managed.
	const isPrintableCharacter = key.length === 1 && key !== " "
	return !isPrintableCharacter || hotkey.keys?.includes(key) === true
}

export interface ShortcutsOptions {
	keys: string[]
	action: (event: KeyboardEvent) => void
	trackingLabel: string
	trackingValue?: (event: KeyboardEvent) => string | undefined
	trackingValues?: (event: KeyboardEvent) => Record<string, any> | undefined
	preventDefault?: boolean
	enableOnFormTags?: HotkeysOptions["enableOnFormTags"]
	eventListenerOptions?: HotkeysOptions["eventListenerOptions"]
	disabled?: boolean
	keyup?: boolean
	keydown?: boolean
	splitKey?: string
	sequenceSplitKey?: string
	delimiter?: string
	ignoreEventWhen?: (event: KeyboardEvent) => boolean
	scope?: string | readonly string[]
	allowInPhotoFullscreen?: boolean
	disableTracking?: boolean
}

const APP_SCOPE = "app"
const DIALOG_SCOPE = "dialog"

export const useShortcuts = (options: ShortcutsOptions) => {
	const dialogs = useAtomValue(dialogsAtom)
	const isPhotoFullscreen = useAtomValue(isPhotoFullscreenAtom)
	const activeScope = dialogs.length > 0 ? DIALOG_SCOPE : APP_SCOPE
	const scopes = Array.isArray(options.scope) ? options.scope : [options.scope ?? APP_SCOPE]
	const isScopeActive = scopes.includes(activeScope)

	useHotkeys(
		options.keys,
		(event, hotkey) => {
			if (!matchesProducedCharacter(event, hotkey)) {
				return
			}

			if (isPhotoFullscreen && !options.allowInPhotoFullscreen) {
				event.preventDefault()
				event.stopPropagation()
				return
			}

			options.action(event)
			if (!options.disableTracking) {
				analytics.trackEvent({
					label: options.trackingLabel,
					value: options.trackingValue?.(event),
					customProperties: options.trackingValues?.(event),
					type: "shortcut",
				})
			}
		},
		{
			enableOnFormTags: options.enableOnFormTags,
			eventListenerOptions: options.eventListenerOptions,
			preventDefault: options.preventDefault
				? (event, hotkey) => matchesProducedCharacter(event, hotkey)
				: false,
			enabled: !options.disabled && isScopeActive,
			keyup: options.keyup,
			keydown: options.keydown,
			splitKey: options.splitKey,
			sequenceSplitKey: options.sequenceSplitKey,
			delimiter: options.delimiter,
			ignoreEventWhen: options.ignoreEventWhen,
			useKey: true,
		},
	)
}
